import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import type { ForceApproveWarning } from './types';

interface ForceApproveWarningDialogProps {
  open: boolean;
  title: string;
  description: string;
  cancelLabel: string;
  approveAnywayLabel: string;
  processing: boolean;
  warnings: ForceApproveWarning[];
  onCancel: () => void;
  onApproveAnyway: () => void;
}

const useStyles = makeStyles(theme => ({
  warningItem: {
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderBottom: 0,
    },
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(0.5),
  },
  warningCode: {
    fontWeight: 700,
  },
}));

export const ForceApproveWarningDialog = ({
  open,
  title,
  description,
  cancelLabel,
  approveAnywayLabel,
  processing,
  warnings,
  onCancel,
  onApproveAnyway,
}: ForceApproveWarningDialogProps) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onCancel}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning">{description}</Alert>

        <Box mt={2}>
          <List disablePadding>
            {warnings.map((warning, index) => (
              <ListItem
                key={`${warning.code}-${warning.field}-${index}`}
                className={classes.warningItem}
              >
                <ListItemText
                  primary={
                    <Box className={classes.warningHeader}>
                      <Typography
                        variant="body2"
                        className={classes.warningCode}
                      >
                        {warning.code}
                      </Typography>

                      <Chip
                        size="small"
                        color={
                          warning.severity === 'error' ? 'secondary' : 'default'
                        }
                        label={warning.severity.toUpperCase()}
                      />

                      {warning.field && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={warning.field}
                        />
                      )}
                    </Box>
                  }
                  secondary={warning.message}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" disabled={processing} onClick={onCancel}>
          {cancelLabel}
        </Button>

        <Button
          color="primary"
          variant="contained"
          disabled={processing}
          onClick={onApproveAnyway}
          startIcon={
            processing ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {approveAnywayLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceApproveWarningDialog;
